/**
 * Админка управления шаблонами карточек объектов.
 *
 * Список шаблонов + создание / редактирование / публикация / архивация / клонирование.
 */

import {
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
  type ChangeEvent,
} from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { cardTemplatesApi } from "@/services/card-api.service";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Plus,
  Pencil,
  Trash2,
  Copy,
  CheckCircle2,
  Archive,
  RotateCcw,
  Info,
  ChevronsUpDown,
  Download,
  Upload,
  HelpCircle,
  GripVertical,
  Sparkles,
} from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import TemplateArchitectChatPanel from "@/components/admin/TemplateArchitectChatPanel";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import type {
  CardTemplate,
  CardTemplateCreate,
  CardTemplateUpdate,
  CardTemplateStatus,
  CardTemplateSectionCreate,
  CardTemplateFieldCreate,
  CardFieldType,
  CardAppliesTo,
  CardTemplateRestoreRequest,
  CardTemplateGeometryRuleCreate,
  GeometrySpatialRuleConfig,
  CardTemplateImportOnConflict,
} from "@/types/card-template";
import {
  FIELD_TYPE_LABELS,
  APPLIES_TO_LABELS,
  STATUS_LABELS,
} from "@/types/card-template";
import { ObjectTypeKlsCombobox } from "@/components/admin/ObjectTypeKlsCombobox";
import { OrganizationKlsCombobox } from "@/components/admin/OrganizationKlsCombobox";
import {
  DEFAULT_SECTION_COLOR_POLICY,
  SECTION_COLOR_POLICY_OPTIONS,
} from "@/components/card/sectionColorPolicies";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { FieldFromProfileWizard } from "@/components/admin/FieldFromProfileWizard";
import { CardTemplateFieldRulesSummary } from "@/components/admin/CardTemplateFieldRulesSummary";
import { CardTemplateFieldRulesEditor } from "@/components/admin/CardTemplateFieldRulesEditor";
import { CardTemplateMetaRulesEditor } from "@/components/admin/CardTemplateMetaRulesEditor";
import {
  CardTemplateFormFieldsSkeleton,
  CardTemplateTableListSkeleton,
} from "@/components/card/shared";
import {
  useFieldWidgetTypesQuery,
  fieldWidgetTypesToLabeledSelectOptions,
} from "@/hooks/useFieldWidgetTypes";

/** Поле формы: id из API нужен для редактора card_field_rules. */
type TemplateFieldForm = CardTemplateFieldCreate & { id?: number };

function parentObjectInheritanceEnabled(f: TemplateFieldForm): boolean {
  const po = f.inheritance_config?.parent_object as { enabled?: boolean } | undefined;
  return Boolean(po?.enabled);
}

/** Секция: id для редактора card_section_rules. */
type SectionForm = CardTemplateSectionCreate & { id?: number };

/** Правило геометрии: id для редактора card_geometry_spatial_rules. */
type GeometryRuleForm = CardTemplateGeometryRuleCreate & { id?: number };

const STATUS_BADGE_VARIANT: Record<CardTemplateStatus, "default" | "secondary" | "destructive"> = {
  draft: "secondary",
  published: "default",
  archived: "destructive",
};

type RestoreMode = "simple" | "replace" | "non_default";

const SECTION_SELECT_AUTO = "__auto__";

/** Ширина боковой панели конструктора шаблона (localStorage). */
const CARD_TEMPLATE_SHEET_WIDTH_KEY = "card-template-sheet-width-v1";
const SHEET_WIDTH_MIN = 360;
const SHEET_WIDTH_PRESETS = { narrow: 480, standard: 768, wide: 1024 } as const;

function maxSheetWidthPx(): number {
  if (typeof window === "undefined") return SHEET_WIDTH_PRESETS.wide * 2;
  return Math.max(SHEET_WIDTH_MIN, Math.floor(window.innerWidth * 0.96));
}

function clampSheetWidth(w: number): number {
  return Math.max(SHEET_WIDTH_MIN, Math.min(w, maxSheetWidthPx()));
}

function readStoredSheetWidth(): number {
  if (typeof window === "undefined") return SHEET_WIDTH_PRESETS.standard;
  try {
    const raw = localStorage.getItem(CARD_TEMPLATE_SHEET_WIDTH_KEY);
    const n = raw ? parseInt(raw, 10) : NaN;
    if (!Number.isFinite(n)) return SHEET_WIDTH_PRESETS.standard;
    return clampSheetWidth(n);
  } catch {
    return SHEET_WIDTH_PRESETS.standard;
  }
}

/** Секции, совместимые с уровнем поля (объект / геометрия). */
function sectionsCompatibleWithField(
  field: CardTemplateFieldCreate,
  all: SectionForm[]
): SectionForm[] {
  if (field.applies_to === "geometry") {
    return all.filter((s) => s.applies_to === "geometry");
  }
  return all.filter((s) => s.applies_to !== "geometry");
}

function defaultSectionCodeForNewField(
  sections: SectionForm[]
): string {
  const obj = sections.filter((s) => s.applies_to !== "geometry");
  const ordered = [...obj].sort(
    (a, b) => (a.display_order ?? 0) - (b.display_order ?? 0)
  );
  return ordered[0]?.code ?? sections[0]?.code ?? "";
}

/** Первая секция, совместимая с уровнем поля (для сброса section_code при смене applies_to). */
function firstCompatibleSectionCode(
  field: Pick<CardTemplateFieldCreate, "applies_to">,
  allSections: SectionForm[],
): string {
  const list = sectionsCompatibleWithField(field as CardTemplateFieldCreate, allSections)
    .slice()
    .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0));
  return list[0]?.code ?? "";
}

/** Элемент списка статусов для ui_config.status_options (поле obj_status) */
type StatusOptionRow = { value: string; label: string };
type FieldDependencyConfig = {
  enabled: boolean;
  depends_on: string;
  equals: boolean;
  mode: "enable" | "hide";
};

function parseStatusOptions(
  ui: Record<string, unknown> | undefined
): StatusOptionRow[] {
  const raw = ui?.status_options;
  if (!Array.isArray(raw)) return [];
  return raw
    .map((row) => {
      if (row && typeof row === "object" && "value" in row && "label" in row) {
        const o = row as { value: unknown; label: unknown };
        return {
          value: String(o.value ?? ""),
          label: String(o.label ?? ""),
        };
      }
      return null;
    })
    .filter((x): x is StatusOptionRow => x !== null && x.value.length > 0);
}

function parseFieldDependencyConfig(
  visibilityRules: Record<string, unknown> | undefined
): FieldDependencyConfig {
  const dependsOn =
    typeof visibilityRules?.depends_on === "string"
      ? visibilityRules.depends_on.trim()
      : "";
  const equals = visibilityRules?.equals === false ? false : true;
  const effect =
    visibilityRules?.effect === "disable" ||
    visibilityRules?.effect === "enable" ||
    visibilityRules?.effect === "hide"
      ? visibilityRules.effect
      : "enable";
  const explicitEnabled = visibilityRules?.enabled === true;
  return {
    enabled:
      explicitEnabled ||
      (dependsOn.length > 0 && (effect === "enable" || effect === "hide")),
    depends_on: dependsOn,
    equals,
    mode: effect === "hide" ? "hide" : "enable",
  };
}

function buildFieldDependencyRules(config: FieldDependencyConfig): Record<string, unknown> {
  if (!config.enabled) return {};
  return {
    enabled: true,
    depends_on: config.depends_on.trim(),
    equals: config.equals,
    effect: config.mode,
  };
}

function ObjStatusOptionsEditor({
  options,
  onChange,
}: {
  options: StatusOptionRow[];
  onChange: (next: StatusOptionRow[]) => void;
}) {
  const setRow = (i: number, patch: Partial<StatusOptionRow>) => {
    const next = options.map((r, j) => (j === i ? { ...r, ...patch } : r));
    onChange(next);
  };
  const removeRow = (i: number) => {
    onChange(options.filter((_, j) => j !== i));
  };

  return (
    <div className="rounded-md border bg-muted/20 p-3 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <Label className="text-xs font-medium">
          Варианты статуса (ui_config.status_options)
        </Label>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="h-7 text-xs"
          onClick={() => onChange([...options, { value: "", label: "" }])}
        >
          <Plus className="h-3 w-3 mr-1" />
          Строка
        </Button>
      </div>
      <p className="text-[11px] text-muted-foreground leading-snug">
        Если список пуст и сохранён без значений — на карточке используются варианты по
        умолчанию (active, inactive, archived, pending). Код — латиница, как в БД.
      </p>
      {options.length === 0 ? (
        <p className="text-xs text-muted-foreground italic">По умолчанию из приложения</p>
      ) : (
        <div className="space-y-1.5">
          {options.map((row, i) => (
            <div key={i} className="flex gap-2 items-center">
              <Input
                className="h-8 text-xs font-mono flex-1"
                placeholder="value (например active)"
                value={row.value}
                onChange={(e) => setRow(i, { value: e.target.value })}
              />
              <Input
                className="h-8 text-xs flex-1"
                placeholder="Подпись"
                value={row.label}
                onChange={(e) => setRow(i, { label: e.target.value })}
              />
              <Button
                type="button"
                size="icon"
                variant="ghost"
                className="h-8 w-8 shrink-0 text-destructive"
                onClick={() => removeRow(i)}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Главный компонент
// ============================================================================

export default function CardTemplateManagement() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [statusFilter, setStatusFilter] = useState<string>("__all__");
  const [globalOnlyFilter, setGlobalOnlyFilter] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [cloneDialogId, setCloneDialogId] = useState<number | null>(null);
  const [cloneCode, setCloneCode] = useState("");
  const [restoreTarget, setRestoreTarget] = useState<CardTemplate | null>(null);
  const [restoreMode, setRestoreMode] = useState<RestoreMode>("simple");
  const [restoreReason, setRestoreReason] = useState("");
  /** null | { id, fromArchive } — fromArchive влияет на текст предупреждения */
  const [deleteTarget, setDeleteTarget] = useState<{
    id: number;
    fromArchive: boolean;
  } | null>(null);
  const importFileInputRef = useRef<HTMLInputElement>(null);
  const [importOnConflict, setImportOnConflict] =
    useState<CardTemplateImportOnConflict>("suffix");

  // ── Queries ────────────────────────────────────────────────────────

  const { data, isLoading } = useQuery({
    queryKey: ["card-templates", statusFilter, globalOnlyFilter],
    queryFn: () =>
      cardTemplatesApi.list({
        status: statusFilter === "__all__" ? undefined : statusFilter,
        global_only: globalOnlyFilter || undefined,
        limit: 200,
      }),
  });

  const templates = data?.items ?? [];

  const handleExportJson = useCallback(
    async (t: CardTemplate) => {
      try {
        const data = await cardTemplatesApi.exportTemplateJson(t.id);
        const blob = new Blob([JSON.stringify(data, null, 2)], {
          type: "application/json;charset=utf-8",
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        const safe = t.code.replace(/[^a-zA-Z0-9_-]+/g, "_");
        a.download = `card-template-${safe}-${t.id}.json`;
        a.click();
        URL.revokeObjectURL(url);
        toast({ title: "JSON экспортирован", description: t.code });
      } catch (err) {
        toast({
          title: "Ошибка экспорта",
          description:
            err instanceof Error ? err.message : "Не удалось скачать файл",
          variant: "destructive",
        });
      }
    },
    [toast]
  );

  const handleImportJsonFile = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      e.target.value = "";
      if (!file) return;
      try {
        const text = await file.text();
        const bundle = JSON.parse(text) as Record<string, unknown>;
        const res = await cardTemplatesApi.importTemplatesJson(bundle, importOnConflict);
        const parts = res.results.map((r) => {
          const idPart = r.template_id != null ? ` → id ${r.template_id}` : "";
          return `${r.code}: ${r.outcome}${idPart}`;
        });
        const hasErr = res.results.some((r) => r.outcome === "error");
        toast({
          title: hasErr ? "Импорт: есть ошибки" : "Импорт выполнен",
          description: parts.join(" · ") || "Нет данных",
          variant: hasErr ? "destructive" : "default",
        });
        queryClient.invalidateQueries({ queryKey: ["card-templates"] });
      } catch (err) {
        toast({
          title: "Ошибка импорта",
          description: err instanceof Error ? err.message : "Неверный JSON или сеть",
          variant: "destructive",
        });
      }
    },
    [importOnConflict, queryClient, toast]
  );

  // ── Mutations ──────────────────────────────────────────────────────

  const publishMutation = useMutation({
    mutationFn: (id: number) => cardTemplatesApi.publish(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["card-templates"] });
      toast({ title: "Шаблон опубликован" });
    },
    onError: (err: Error) =>
      toast({ title: "Ошибка", description: err.message, variant: "destructive" }),
  });

  const archiveMutation = useMutation({
    mutationFn: (id: number) => cardTemplatesApi.archive(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["card-templates"] });
      toast({ title: "Шаблон архивирован" });
    },
    onError: (err: Error) =>
      toast({ title: "Ошибка", description: err.message, variant: "destructive" }),
  });

  const cloneMutation = useMutation({
    mutationFn: ({ id, code }: { id: number; code: string }) =>
      cardTemplatesApi.clone(id, code),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["card-templates"] });
      toast({ title: "Шаблон клонирован" });
      setCloneDialogId(null);
      setCloneCode("");
    },
    onError: (err: Error) =>
      toast({ title: "Ошибка", description: err.message, variant: "destructive" }),
  });

  const restoreMutation = useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: number;
      payload: CardTemplateRestoreRequest;
    }) => cardTemplatesApi.restore(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["card-templates"] });
      toast({ title: "Шаблон восстановлен из архива" });
      setRestoreTarget(null);
      setRestoreReason("");
      setRestoreMode("simple");
    },
    onError: (err: Error) =>
      toast({ title: "Ошибка", description: err.message, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => cardTemplatesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["card-templates"] });
      toast({ title: "Шаблон удалён" });
      setDeleteTarget(null);
    },
    onError: (err: Error) =>
      toast({ title: "Ошибка", description: err.message, variant: "destructive" }),
  });

  const handleOpenCreate = useCallback(() => {
    setEditingId(null);
    setDialogOpen(true);
  }, []);

  const handleOpenEdit = useCallback((id: number) => {
    setEditingId(id);
    setDialogOpen(true);
  }, []);

  const handleDialogClose = useCallback(() => {
    setDialogOpen(false);
    setEditingId(null);
  }, []);

  const formatDate = (d?: string | null) =>
    d ? new Date(d).toLocaleDateString("ru-RU") : "—";

  // ── Render ─────────────────────────────────────────────────────────

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center gap-3">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Все статусы" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Все</SelectItem>
            <SelectItem value="draft">Черновик</SelectItem>
            <SelectItem value="published">Опубликован</SelectItem>
            <SelectItem value="archived">Архив</SelectItem>
          </SelectContent>
        </Select>

        <div className="flex items-center gap-2">
          <Checkbox
            id="card-tpl-global-only"
            checked={globalOnlyFilter}
            onCheckedChange={(v) => setGlobalOnlyFilter(v === true)}
          />
          <Label htmlFor="card-tpl-global-only" className="text-sm font-normal cursor-pointer">
            Только универсальные
          </Label>
        </div>

        <input
          ref={importFileInputRef}
          type="file"
          accept="application/json,.json"
          className="hidden"
          onChange={handleImportJsonFile}
        />
        <Select
          value={importOnConflict}
          onValueChange={(v) => setImportOnConflict(v as CardTemplateImportOnConflict)}
        >
          <SelectTrigger className="w-[220px] h-9 text-xs">
            <SelectValue placeholder="Конфликт code" />
          </SelectTrigger>
          <SelectContent className="z-[250]">
            <SelectItem value="error">Импорт: конфликт code → ошибка</SelectItem>
            <SelectItem value="skip">Импорт: конфликт code → пропуск</SelectItem>
            <SelectItem value="suffix">Импорт: конфликт code → суффикс _import</SelectItem>
          </SelectContent>
        </Select>
        <Button
          size="sm"
          variant="outline"
          type="button"
          onClick={() => importFileInputRef.current?.click()}
        >
          <Upload className="h-4 w-4 mr-1" />
          Импорт JSON
        </Button>

        <Button size="sm" onClick={handleOpenCreate}>
          <Plus className="h-4 w-4 mr-1" />
          Создать шаблон
        </Button>
      </div>

      {/* Table */}
      {isLoading ? (
        <CardTemplateTableListSkeleton rows={4} />
      ) : templates.length === 0 ? (
        <p className="text-sm text-muted-foreground py-8 text-center">
          Шаблоны не найдены
        </p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Код</TableHead>
              <TableHead>Название</TableHead>
              <TableHead>Тип объекта</TableHead>
              <TableHead>Статус</TableHead>
              <TableHead className="text-center">Версия</TableHead>
              <TableHead className="text-center">Дефолт</TableHead>
              <TableHead>Опубликован</TableHead>
              <TableHead className="text-right">Действия</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {templates.map((t) => (
              <TableRow key={t.id}>
                <TableCell className="font-mono text-xs">{t.code}</TableCell>
                <TableCell>{t.name}</TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {t.object_kls_id == null ? (
                    <span className="text-foreground">Все типы</span>
                  ) : (
                    <>KLS {t.object_kls_id}</>
                  )}
                </TableCell>
                <TableCell>
                  <Badge variant={STATUS_BADGE_VARIANT[t.status]}>
                    {STATUS_LABELS[t.status]}
                  </Badge>
                </TableCell>
                <TableCell className="text-center">{t.version}</TableCell>
                <TableCell className="text-center">
                  {t.is_default ? "✓" : ""}
                </TableCell>
                <TableCell>{formatDate(t.published_at)}</TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7"
                      title="Редактировать"
                      onClick={() => handleOpenEdit(t.id)}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>

                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7"
                      title="Экспорт JSON (резервная копия)"
                      type="button"
                      onClick={() => handleExportJson(t)}
                    >
                      <Download className="h-3.5 w-3.5" />
                    </Button>

                    {t.status === "draft" && (
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7 text-green-600"
                        title="Опубликовать"
                        onClick={() => publishMutation.mutate(t.id)}
                        disabled={publishMutation.isPending}
                      >
                        <CheckCircle2 className="h-3.5 w-3.5" />
                      </Button>
                    )}

                    {t.status === "published" && (
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7 text-amber-600"
                        title="Архивировать"
                        onClick={() => archiveMutation.mutate(t.id)}
                        disabled={archiveMutation.isPending}
                      >
                        <Archive className="h-3.5 w-3.5" />
                      </Button>
                    )}

                    {t.status === "archived" && (
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7 text-emerald-600"
                        title="Восстановить из архива"
                        onClick={() => {
                          setRestoreTarget(t);
                          setRestoreMode("simple");
                          setRestoreReason("");
                        }}
                      >
                        <RotateCcw className="h-3.5 w-3.5" />
                      </Button>
                    )}

                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7"
                      title="Клонировать"
                      onClick={() => {
                        setCloneDialogId(t.id);
                        setCloneCode(`${t.code}_copy`);
                      }}
                    >
                      <Copy className="h-3.5 w-3.5" />
                    </Button>

                    {t.status === "draft" && (
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7 text-destructive"
                        title="Удалить черновик"
                        onClick={() => setDeleteTarget({ id: t.id, fromArchive: false })}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}

                    {t.status === "archived" && (
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7 text-destructive"
                        title="Удалить из базы (необратимо)"
                        onClick={() => setDeleteTarget({ id: t.id, fromArchive: true })}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {/* Restore from archive */}
      <Dialog
        open={restoreTarget !== null}
        onOpenChange={(open) => {
          if (!open) {
            setRestoreTarget(null);
            setRestoreReason("");
            setRestoreMode("simple");
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Восстановить из архива</DialogTitle>
          </DialogHeader>
          {restoreTarget && (
            <div className="space-y-4 py-2">
              <p className="text-sm text-muted-foreground">
                Шаблон{" "}
                <span className="font-mono text-foreground">{restoreTarget.code}</span>
                {restoreTarget.is_default ? (
                  <>
                    {" "}
                    помечен как «по умолчанию». Если для той же связки типа объекта и
                    организации уже есть опубликованный default, выберите стратегию
                    ниже (включая универсальный шаблон).
                  </>
                ) : (
                  <> будет снова опубликован.</>
                )}
              </p>

              {restoreTarget.is_default && (
                <RadioGroup
                  value={restoreMode}
                  onValueChange={(v) => setRestoreMode(v as RestoreMode)}
                  className="gap-3"
                >
                  <div className="flex items-start gap-2">
                    <RadioGroupItem value="simple" id="restore-simple" className="mt-0.5" />
                    <Label htmlFor="restore-simple" className="font-normal leading-snug cursor-pointer">
                      Обычное восстановление. При конфликте с действующим default сервер
                      вернёт ошибку — тогда выберите другой вариант или клонируйте шаблон.
                    </Label>
                  </div>
                  <div className="flex items-start gap-2">
                    <RadioGroupItem value="replace" id="restore-replace" className="mt-0.5" />
                    <Label htmlFor="restore-replace" className="font-normal leading-snug cursor-pointer">
                      Заменить default: снять «по умолчанию» с текущего опубликованного
                      шаблона в этой ячейке и восстановить этот.
                    </Label>
                  </div>
                  <div className="flex items-start gap-2">
                    <RadioGroupItem value="non_default" id="restore-nod" className="mt-0.5" />
                    <Label htmlFor="restore-nod" className="font-normal leading-snug cursor-pointer">
                      Восстановить без признака «по умолчанию» (конфликт индекса не возникнет).
                    </Label>
                  </div>
                </RadioGroup>
              )}

              <div>
                <Label htmlFor="restore-reason">Комментарий (необязательно)</Label>
                <Textarea
                  id="restore-reason"
                  value={restoreReason}
                  onChange={(e) => setRestoreReason(e.target.value)}
                  rows={2}
                  className="mt-1.5"
                  placeholder="Причина восстановления"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setRestoreTarget(null);
                setRestoreReason("");
                setRestoreMode("simple");
              }}
            >
              Отмена
            </Button>
            <Button
              onClick={() => {
                if (!restoreTarget) return;
                const payload: CardTemplateRestoreRequest = {
                  reason: restoreReason.trim() || null,
                  replace_default: restoreMode === "replace",
                  restore_as_non_default: restoreMode === "non_default",
                };
                restoreMutation.mutate({ id: restoreTarget.id, payload });
              }}
              disabled={restoreMutation.isPending}
            >
              {restoreMutation.isPending ? "Восстановление..." : "Восстановить"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Clone Dialog */}
      <Dialog open={cloneDialogId !== null} onOpenChange={() => setCloneDialogId(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Клонировать шаблон</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="clone-code">Код нового шаблона</Label>
            <Input
              id="clone-code"
              value={cloneCode}
              onChange={(e) => setCloneCode(e.target.value)}
              className="mt-1.5"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCloneDialogId(null)}>
              Отмена
            </Button>
            <Button
              onClick={() =>
                cloneDialogId &&
                cloneMutation.mutate({ id: cloneDialogId, code: cloneCode })
              }
              disabled={!cloneCode.trim() || cloneMutation.isPending}
            >
              Клонировать
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog
        open={deleteTarget !== null}
        onOpenChange={() => setDeleteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Удалить шаблон?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm text-muted-foreground">
                {deleteTarget?.fromArchive ? (
                  <>
                    <p>
                      Вы удаляете <strong className="text-foreground">архивную</strong>{" "}
                      запись целиком из базы. Это действие необратимо.
                    </p>
                    <p>
                      Вместе с шаблоном будут удалены секции, поля и вся история смен
                      статусов в журнале публикаций (
                      <span className="font-mono text-foreground">publish_log</span>
                      ). След в виде архивной строки исчезнет — иногда разумнее{" "}
                      <strong className="text-foreground">оставить архив</strong> как
                      учётный след и не удалять.
                    </p>
                    <p>
                      Данные объектов (KLS-параметры) не привязаны к id шаблона и не
                      удалятся; изменится только подбор карточки в интерфейсе, если
                      других шаблонов не останется.
                    </p>
                  </>
                ) : (
                  <p>
                    Черновик будет удалён безвозвратно вместе с несохранённой в
                    опубликованном виде структурой. Восстановить его нельзя.
                  </p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction
              onClick={() =>
                deleteTarget && deleteMutation.mutate(deleteTarget.id)
              }
            >
              {deleteTarget?.fromArchive ? "Удалить навсегда" : "Удалить"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Create / Edit Dialog */}
      {dialogOpen && (
        <TemplateFormDialog
          editingId={editingId}
          onClose={handleDialogClose}
        />
      )}
    </div>
  );
}

// ============================================================================
// Форма создания / редактирования шаблона (боковая панель Sheet + вкладки).
// Редакторы мета-правил остаются на Radix Dialog с nestedAboveSheet (z-[230]),
// чтобы не оказываться под панелью Sheet (z-[220]).
// ============================================================================

interface TemplateFormDialogProps {
  editingId: number | null;
  onClose: () => void;
}

function TemplateFormDialog({ editingId, onClose }: TemplateFormDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const isEditing = editingId !== null;

  const { data: existingTemplate, isLoading: isLoadingTemplate } = useQuery({
    queryKey: ["card-templates", editingId],
    queryFn: () => cardTemplatesApi.get(editingId!),
    enabled: isEditing,
  });

  const fieldWidgetTypesQ = useFieldWidgetTypesQuery({ staleTime: 5 * 60_000 });

  const fieldTypeSelectOptions = useMemo(
    () =>
      fieldWidgetTypesToLabeledSelectOptions(
        fieldWidgetTypesQ.data,
        FIELD_TYPE_LABELS,
        Object.keys(FIELD_TYPE_LABELS) as CardFieldType[],
      ),
    [fieldWidgetTypesQ.data],
  );

  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [objectKlsId, setObjectKlsId] = useState<number | "">("");
  /** Универсальный шаблон: object_kls_id = null, подходит всем типам объектов. */
  const [isUniversal, setIsUniversal] = useState(false);
  const [organizationKlsId, setOrganizationKlsId] = useState<number | "">("");
  const [isDefault, setIsDefault] = useState(false);
  const [sections, setSections] = useState<SectionForm[]>([]);
  const [fields, setFields] = useState<TemplateFieldForm[]>([]);
  /** Правила min/max числа геометрий по семантическому типу (этап B, админка). */
  const [geometryRules, setGeometryRules] = useState<GeometryRuleForm[]>([]);
  const [initialized, setInitialized] = useState(!isEditing);
  const [profileWizardOpen, setProfileWizardOpen] = useState(false);
  const [applyingProfile, setApplyingProfile] = useState(false);
  const [rulesTarget, setRulesTarget] = useState<{ id: number; label: string } | null>(null);
  const [sectionRulesTarget, setSectionRulesTarget] = useState<{
    id: number;
    label: string;
  } | null>(null);
  const [geometrySpatialTarget, setGeometrySpatialTarget] = useState<{
    id: number;
    label: string;
  } | null>(null);

  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);
  const [sheetWidthPx, setSheetWidthPx] = useState(readStoredSheetWidth);
  const formBaselineRef = useRef<string | null>(null);

  useEffect(() => {
    const onWin = () => setSheetWidthPx((w) => clampSheetWidth(w));
    window.addEventListener("resize", onWin);
    return () => window.removeEventListener("resize", onWin);
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(CARD_TEMPLATE_SHEET_WIDTH_KEY, String(sheetWidthPx));
    } catch {
      /* ignore quota / private mode */
    }
  }, [sheetWidthPx]);

  useEffect(() => {
    if (!initialized) return;
    formBaselineRef.current = JSON.stringify({
      code,
      name,
      description,
      isUniversal,
      objectKlsId,
      organizationKlsId,
      isDefault,
      sections,
      fields,
      geometryRules,
    });
  }, [initialized]);

  const snapshotForm = useCallback(() => {
    return JSON.stringify({
      code,
      name,
      description,
      isUniversal,
      objectKlsId,
      organizationKlsId,
      isDefault,
      sections,
      fields,
      geometryRules,
    });
  }, [
    code,
    name,
    description,
    isUniversal,
    objectKlsId,
    organizationKlsId,
    isDefault,
    sections,
    fields,
    geometryRules,
  ]);

  const requestClose = useCallback(() => {
    if (!initialized) {
      onClose();
      return;
    }
    const now = snapshotForm();
    if (formBaselineRef.current !== null && now !== formBaselineRef.current) {
      setConfirmCloseOpen(true);
      return;
    }
    onClose();
  }, [initialized, onClose, snapshotForm]);

  const handleResizePointerDown = useCallback(
    (e: { preventDefault: () => void; clientX: number }) => {
      e.preventDefault();
      const startX = e.clientX;
      const startW = sheetWidthPx;
      const onMove = (ev: PointerEvent) => {
        setSheetWidthPx(clampSheetWidth(startW + (startX - ev.clientX)));
      };
      const onUp = () => {
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
      };
      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
    },
    [sheetWidthPx],
  );

  const templateStatus = existingTemplate?.status;
  const isDraftTemplate = !isEditing || templateStatus === "draft";

  if (isEditing && existingTemplate && !initialized) {
    setCode(existingTemplate.code);
    setName(existingTemplate.name);
    setDescription(existingTemplate.description ?? "");
    const oid = existingTemplate.object_kls_id;
    setIsUniversal(oid == null);
    setObjectKlsId(oid == null ? "" : oid);
    setOrganizationKlsId(existingTemplate.organization_kls_id ?? "");
    setIsDefault(existingTemplate.is_default);
    setSections(
      existingTemplate.sections.map((s) => ({
        id: s.id,
        code: s.code,
        name: s.name,
        applies_to: s.applies_to,
        display_order: s.display_order,
        is_collapsible: s.is_collapsible,
        is_default_open: s.is_default_open,
        ui_config: s.ui_config ?? {},
      }))
    );
    setFields(
      existingTemplate.fields.map((f) => {
        const sec = existingTemplate.sections.find((s) => s.id === f.section_id);
        return {
          id: f.id,
          field_key: f.field_key,
          label: f.label,
          description: f.description,
          applies_to: f.applies_to,
          field_type: f.field_type,
          field_type_kls_id: f.field_type_kls_id ?? undefined,
          qual_id: f.qual_id,
          kls_id: f.kls_id,
          geometry_type_kls_id: f.geometry_type_kls_id,
          section_code: sec?.code ?? "",
          is_required: f.is_required,
          is_readonly: f.is_readonly,
          is_multiple: f.is_multiple,
          is_active: f.is_active,
          display_order: f.display_order,
          validation_rules: f.validation_rules,
          ui_config: f.ui_config,
          visibility_rules: f.visibility_rules,
          permissions_config: f.permissions_config ?? {},
          inherit_from_object: f.inherit_from_object ?? false,
          inheritance_config: f.inheritance_config ?? undefined,
          rule_profile_id: f.rule_profile_id ?? undefined,
        };
      })
    );
    setGeometryRules(
      (existingTemplate.geometry_rules ?? []).map((r) => ({
        id: r.id,
        geometry_semantic_kls_id: r.geometry_semantic_kls_id,
        min_count: r.min_count ?? 0,
        max_count: r.max_count ?? null,
        is_required: r.is_required ?? false,
        display_order: r.display_order ?? 100,
        spatial_config: r.spatial_config ?? undefined,
      }))
    );
    setInitialized(true);
  }

  const createMutation = useMutation({
    mutationFn: (data: CardTemplateCreate) => cardTemplatesApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["card-templates"] });
      toast({ title: "Шаблон создан" });
      onClose();
    },
    onError: (err: Error) =>
      toast({ title: "Ошибка", description: err.message, variant: "destructive" }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: CardTemplateUpdate }) =>
      cardTemplatesApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["card-templates"] });
      toast({ title: "Шаблон обновлён" });
      onClose();
    },
    onError: (err: Error) =>
      toast({ title: "Ошибка", description: err.message, variant: "destructive" }),
  });

  const applyFieldFromProfile = async (
    newField: CardTemplateFieldCreate,
    profileId: number
  ) => {
    if (!editingId) return;
    setApplyingProfile(true);
    try {
      const nextFields: TemplateFieldForm[] = [...fields, newField];
      const data: CardTemplateUpdate = {
        name: name.trim(),
        description: description.trim() || null,
        object_kls_id: isUniversal ? null : Number(objectKlsId),
        organization_kls_id: organizationKlsId ? Number(organizationKlsId) : null,
        is_default: isDefault,
        sections: sections.map(({ id: _sid, ...s }) => s),
        fields: nextFields.map(({ section_id: _ignored, id: _fid, ...f }) => ({
          ...f,
          section_code: (f.section_code ?? "").trim() || null,
          section_id: null,
        })),
        geometry_rules: geometryRules.map(({ id: _gid, ...g }) => g),
      };
      await cardTemplatesApi.update(editingId, data);
      const refreshed = await cardTemplatesApi.get(editingId);
      const created = refreshed.fields.find((ff) => ff.field_key === newField.field_key);
      if (!created?.id) {
        throw new Error("Не найдено новое поле после сохранения");
      }
      await cardTemplatesApi.applyRuleProfileToFields(editingId, {
        profile_id: profileId,
        field_ids: [created.id],
      });
      await queryClient.invalidateQueries({ queryKey: ["card-templates", editingId] });
      await queryClient.refetchQueries({ queryKey: ["card-templates", editingId] });
      setInitialized(false);
      toast({
        title: "Поле добавлено",
        description: "Профиль правил применён к новому полю.",
      });
    } finally {
      setApplyingProfile(false);
    }
  };

  const handleSubmit = () => {
    if (!code.trim() || !name.trim()) {
      toast({
        title: "Заполните обязательные поля",
        description: "Код и название обязательны",
        variant: "destructive",
      });
      return;
    }
    if (!isUniversal && (objectKlsId === "" || Number(objectKlsId) <= 0)) {
      toast({
        title: "Укажите тип объекта",
        description:
          "Снимите «Универсальный шаблон» и выберите тип объекта в списке (qual 354), либо оставьте универсальный шаблон для всех типов объектов.",
        variant: "destructive",
      });
      return;
    }

    const payload = {
      code: code.trim(),
      name: name.trim(),
      description: description.trim() || null,
      object_kls_id: isUniversal ? null : Number(objectKlsId),
      organization_kls_id: organizationKlsId ? Number(organizationKlsId) : null,
      is_default: isDefault,
      sections: sections.map(({ id: _sid, ...s }) => s),
      fields: fields.map(({ section_id: _ignored, id: _fid, ...f }) => ({
        ...f,
        section_code: (f.section_code ?? "").trim() || null,
        section_id: null,
      })),
      geometry_rules: geometryRules.map(({ id: _gid, ...g }) => g),
    };

    if (isEditing && editingId) {
      updateMutation.mutate({ id: editingId, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  // ── Секции ──────────────────────────────────────────────────────

  const addSection = () => {
    setSections((prev) => [
      ...prev,
      {
        code: `section_${prev.length + 1}`,
        name: "",
        applies_to: "object" as CardAppliesTo,
        display_order: (prev.length + 1) * 10,
        is_collapsible: false,
        is_default_open: true,
        ui_config: { color_policy: "neutral" },
      },
    ]);
  };

  const removeSection = (idx: number) => {
    setSections((prev) => prev.filter((_, i) => i !== idx));
  };

  const updateSection = (idx: number, patch: Partial<SectionForm>) => {
    setSections((prev) =>
      prev.map((s, i) => (i === idx ? { ...s, ...patch } : s))
    );
  };

  // ── Поля ────────────────────────────────────────────────────────

  const addField = () => {
    setFields((prev) => [
      ...prev,
      {
        field_key: `field_${prev.length + 1}`,
        label: "",
        field_type: "string" as CardFieldType,
        qual_id: 0,
        applies_to: "object" as CardAppliesTo,
        section_code: defaultSectionCodeForNewField(sections),
        is_required: false,
        is_readonly: false,
        is_multiple: false,
        is_active: true,
        display_order: (prev.length + 1) * 10,
        validation_rules: {},
        ui_config: {},
        visibility_rules: {},
        permissions_config: {},
        inherit_from_object: false,
        inheritance_config: undefined,
      },
    ]);
  };

  const addGeometryRule = () => {
    setGeometryRules((prev) => [
      ...prev,
      {
        geometry_semantic_kls_id: 0,
        min_count: 0,
        max_count: null,
        is_required: false,
        display_order: (prev.length + 1) * 10,
      },
    ]);
  };

  const updateGeometryRule = (idx: number, patch: Partial<GeometryRuleForm>) => {
    setGeometryRules((prev) =>
      prev.map((r, i) => (i === idx ? { ...r, ...patch } : r))
    );
  };

  const removeGeometryRule = (idx: number) => {
    setGeometryRules((prev) => prev.filter((_, i) => i !== idx));
  };

  const removeField = (idx: number) => {
    setFields((prev) => prev.filter((_, i) => i !== idx));
  };

  const updateField = (idx: number, patch: Partial<CardTemplateFieldCreate>) => {
    setFields((prev) =>
      prev.map((f, i) => (i === idx ? { ...f, ...patch } : f))
    );
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <>
      <Sheet
        open
        onOpenChange={(open) => {
          if (!open) requestClose();
        }}
      >
        <SheetContent
          side="right"
          className="flex h-full max-w-none flex-col gap-0 overflow-hidden border-l p-0 sm:max-w-none data-[state=open]:slide-in-from-right"
          style={{ width: `${sheetWidthPx}px` }}
        >
          <SheetHeader className="shrink-0 space-y-3 border-b px-6 pb-4 pr-14 pt-4 text-left">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <SheetTitle>
                {isEditing ? "Редактирование шаблона" : "Создание шаблона"}
              </SheetTitle>
              <div className="flex flex-wrap items-center gap-1">
                <span className="text-muted-foreground mr-1 text-xs">Ширина</span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() =>
                    setSheetWidthPx(clampSheetWidth(SHEET_WIDTH_PRESETS.narrow))
                  }
                >
                  Узкая
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() =>
                    setSheetWidthPx(clampSheetWidth(SHEET_WIDTH_PRESETS.standard))
                  }
                >
                  Стандарт
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() =>
                    setSheetWidthPx(clampSheetWidth(SHEET_WIDTH_PRESETS.wide))
                  }
                >
                  Широкая
                </Button>
              </div>
            </div>
          </SheetHeader>

          {isEditing && isLoadingTemplate ? (
            <CardTemplateFormFieldsSkeleton />
          ) : (
            <Tabs defaultValue="general" className="flex min-h-0 flex-1 flex-col">
              <TabsList
                className="mx-6 mt-3 h-auto shrink-0 flex-wrap justify-start gap-1 rounded-lg bg-muted p-1"
                aria-label="Разделы конструктора шаблона"
              >
                <Tooltip>
                  <TooltipTrigger asChild>
                    <TabsTrigger value="general" className="gap-1">
                      Общее
                      <HelpCircle className="h-3.5 w-3.5 opacity-60" />
                    </TabsTrigger>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-xs text-left">
                    Код, название, тип объекта и организация. Значения для карточек объектов
                    задаются позже, не здесь.
                  </TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <TabsTrigger value="sections" className="gap-1">
                      Секции
                      <HelpCircle className="h-3.5 w-3.5 opacity-60" />
                    </TabsTrigger>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-xs text-left">
                    Блоки карточки: код секции, уровень (объект / геометрия), оформление.
                  </TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <TabsTrigger value="geometries" className="gap-1">
                      Геометрии
                      <HelpCircle className="h-3.5 w-3.5 opacity-60" />
                    </TabsTrigger>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-xs text-left">
                    Минимум и максимум геометрий по семантическому типу (KLS), пространственные
                    ограничения.
                  </TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <TabsTrigger value="fields" className="gap-1">
                      Поля
                      <HelpCircle className="h-3.5 w-3.5 opacity-60" />
                    </TabsTrigger>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-xs text-left">
                    Поля карточки: field_key, тип виджета, qual_id, привязка к секции и правилам.
                  </TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <TabsTrigger value="assistant" className="gap-1">
                      <Sparkles className="h-3.5 w-3.5 opacity-80" />
                      Ассистент
                    </TabsTrigger>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-xs text-left">
                    ИИ-агент «Архитектор шаблонов»: справочники KLS, список шаблонов, подсказки по структуре карточки.
                    Требуются права на чат ИИ и управление шаблонами.
                  </TooltipContent>
                </Tooltip>
              </TabsList>

              <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-4 pt-2">
                <TabsContent value="general" className="mt-0 space-y-6 focus-visible:outline-none">
            <Alert className="border-muted-foreground/20 bg-muted/50">
              <Info className="h-4 w-4" />
              <AlertDescription className="text-muted-foreground">
                <strong className="text-foreground">С чего начать.</strong> Задайте код и
                название, при необходимости тип объекта и организацию. На вкладках{" "}
                <strong className="text-foreground">Секции</strong>,{" "}
                <strong className="text-foreground">Геометрии</strong> и{" "}
                <strong className="text-foreground">Поля</strong> опишите структуру карточки;
                на вкладке <strong className="text-foreground">Ассистент</strong> доступен ИИ-архитектор
                (KLS и шаблоны). Значения атрибутов для объектов заполняются на карточке объекта, не здесь.
                Черновик сохраните кнопкой внизу, затем опубликуйте в списке шаблонов.
              </AlertDescription>
            </Alert>

            {/* Основные поля */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="tpl-code">Код *</Label>
                <Input
                  id="tpl-code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="my_template"
                  disabled={isEditing}
                />
              </div>
              <div>
                <Label htmlFor="tpl-name">Название *</Label>
                <Input
                  id="tpl-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="tpl-desc">Описание</Label>
              <Textarea
                id="tpl-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
              />
            </div>

            <div className="flex items-center gap-2 pb-1">
              <Checkbox
                id="tpl-universal"
                checked={isUniversal}
                onCheckedChange={(v) => {
                  const on = v === true;
                  setIsUniversal(on);
                  if (on) setObjectKlsId("");
                }}
              />
              <Label htmlFor="tpl-universal" className="text-sm font-normal cursor-pointer">
                Универсальный шаблон (все типы объектов)
              </Label>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="min-w-0">
                <Label>
                  Тип объекта{!isUniversal ? " *" : ""}
                </Label>
                <div className="mt-1.5">
                  <ObjectTypeKlsCombobox
                    value={objectKlsId}
                    onValueChange={setObjectKlsId}
                    disabled={isUniversal}
                  />
                </div>
                {!isUniversal && (
                  <p className="text-xs text-muted-foreground mt-1.5">
                    Справочник квалификатора 354. Поиск по названию и коду.
                  </p>
                )}
              </div>
              <div className="min-w-0">
                <Label>Организация (KLS)</Label>
                <div className="mt-1.5">
                  <OrganizationKlsCombobox
                    value={organizationKlsId}
                    onValueChange={setOrganizationKlsId}
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-1.5">
                  Справочник квалификатора 336. Поиск по названию и коду. Пусто — шаблон
                  для всех организаций.
                </p>
              </div>
              <div className="flex items-end gap-2 pb-0.5">
                <Checkbox
                  id="tpl-default"
                  checked={isDefault}
                  onCheckedChange={(v) => setIsDefault(v === true)}
                />
                <Label htmlFor="tpl-default">По умолчанию</Label>
              </div>
            </div>

                </TabsContent>

                <TabsContent value="sections" className="mt-0 space-y-6 focus-visible:outline-none">
            {/* Секции */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-semibold">Секции</h4>
                <Button size="sm" variant="outline" onClick={addSection}>
                  <Plus className="h-3.5 w-3.5 mr-1" />
                  Добавить
                </Button>
              </div>
              {sections.length === 0 ? (
                <p className="text-xs text-muted-foreground">Секций нет</p>
              ) : (
                <div className="space-y-2">
                  {sections.map((s, idx) => (
                    <div key={idx} className="border rounded-md p-2 space-y-2">
                    <div
                      className="grid grid-cols-[1fr_1fr_140px_220px_auto] gap-2 items-end"
                    >
                      <div>
                        <Label className="text-xs">Код</Label>
                        <Input
                          value={s.code}
                          onChange={(e) =>
                            updateSection(idx, { code: e.target.value })
                          }
                          className="h-8 text-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Название</Label>
                        <Input
                          value={s.name}
                          onChange={(e) =>
                            updateSection(idx, { name: e.target.value })
                          }
                          className="h-8 text-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Уровень</Label>
                        <Select
                          value={s.applies_to ?? "object"}
                          onValueChange={(v) =>
                            updateSection(idx, {
                              applies_to: v as CardAppliesTo,
                            })
                          }
                        >
                          <SelectTrigger className="h-8 text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(APPLIES_TO_LABELS).map(
                              ([val, label]) => (
                                <SelectItem key={val} value={val}>
                                  {label}
                                </SelectItem>
                              )
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <div>
                          <Label className="text-xs">Цветовая политика</Label>
                          <Select
                            value={
                              (s.ui_config as { color_policy?: string } | undefined)
                                ?.color_policy ?? DEFAULT_SECTION_COLOR_POLICY
                            }
                            onValueChange={(v) =>
                              updateSection(idx, {
                                ui_config: {
                                  ...(s.ui_config ?? {}),
                                  color_policy: v,
                                },
                              })
                            }
                          >
                            <SelectTrigger className="h-8 text-sm mt-1">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="max-h-72">
                              {SECTION_COLOR_POLICY_OPTIONS.map((opt) => (
                                <SelectItem key={opt.value} value={opt.value}>
                                  {opt.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex items-center gap-2">
                          <Checkbox
                            id={`tpl-sec-coll-${idx}`}
                            checked={s.is_collapsible ?? false}
                            onCheckedChange={(v) =>
                              updateSection(idx, { is_collapsible: v === true })
                            }
                          />
                          <Label htmlFor={`tpl-sec-coll-${idx}`} className="text-xs font-normal">
                            Сворачиваемая
                          </Label>
                        </div>
                        <div className="flex items-center gap-2">
                          <Checkbox
                            id={`tpl-sec-open-${idx}`}
                            checked={s.is_default_open ?? true}
                            disabled={!(s.is_collapsible ?? false)}
                            onCheckedChange={(v) =>
                              updateSection(idx, { is_default_open: v === true })
                            }
                          />
                          <Label htmlFor={`tpl-sec-open-${idx}`} className="text-xs font-normal">
                            По умолчанию раскрыта
                          </Label>
                        </div>
                      </div>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 text-destructive"
                        onClick={() => removeSection(idx)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                    {isEditing &&
                      isDraftTemplate &&
                      editingId != null &&
                      s.id != null && (
                        <div className="flex justify-end">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="text-xs"
                            onClick={() =>
                              setSectionRulesTarget({
                                id: s.id!,
                                label: s.name?.trim() || s.code,
                              })
                            }
                          >
                            Мета-правила секции
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

                </TabsContent>

                <TabsContent value="geometries" className="mt-0 space-y-6 focus-visible:outline-none">
            {/* Правила кардинальности геометрий (шаблон) */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-semibold">Правила геометрий (min/max)</h4>
                <Button size="sm" variant="outline" type="button" onClick={addGeometryRule}>
                  <Plus className="h-3.5 w-3.5 mr-1" />
                  Правило
                </Button>
              </div>
              {geometryRules.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  Не задано — ограничения только на уровне сервиса. Добавьте строку для семантического
                  типа геометрии (kls_id семантики, qual геометрий).
                </p>
              ) : (
                <div className="space-y-2">
                  {geometryRules.map((gr, gidx) => (
                    <div key={gidx} className="border rounded-md p-2 space-y-2">
                      <div className="grid grid-cols-[1fr_80px_80px_100px_auto] gap-2 items-end">
                        <div>
                          <Label className="text-xs">semantic kls_id</Label>
                          <Input
                            type="number"
                            className="h-8 text-sm mt-1"
                            value={gr.geometry_semantic_kls_id || ""}
                            onChange={(e) =>
                              updateGeometryRule(gidx, {
                                geometry_semantic_kls_id: Number(e.target.value) || 0,
                              })
                            }
                          />
                        </div>
                        <div>
                          <Label className="text-xs">min</Label>
                          <Input
                            type="number"
                            className="h-8 text-sm mt-1"
                            value={gr.min_count ?? 0}
                            onChange={(e) =>
                              updateGeometryRule(gidx, {
                                min_count: Number(e.target.value) || 0,
                              })
                            }
                          />
                        </div>
                        <div>
                          <Label className="text-xs">max</Label>
                          <Input
                            type="number"
                            className="h-8 text-sm mt-1"
                            placeholder="∞"
                            value={gr.max_count ?? ""}
                            onChange={(e) => {
                              const t = e.target.value.trim();
                              updateGeometryRule(gidx, {
                                max_count: t === "" ? null : Number(t),
                              });
                            }}
                          />
                        </div>
                        <div className="flex items-center gap-2 pb-1">
                          <Checkbox
                            id={`gr-req-${gidx}`}
                            checked={gr.is_required ?? false}
                            onCheckedChange={(v) =>
                              updateGeometryRule(gidx, { is_required: v === true })
                            }
                          />
                          <Label htmlFor={`gr-req-${gidx}`} className="text-xs font-normal">
                            Обяз.
                          </Label>
                        </div>
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8 text-destructive"
                          onClick={() => removeGeometryRule(gidx)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">
                          spatial_config (JSON, этап 2) — must_be_inside, cannot_overlap, …
                        </Label>
                        <Textarea
                          className="font-mono text-xs min-h-[72px] mt-1"
                          placeholder='{"must_be_inside":[123],"area_bounds":{"min":1,"max":50000}}'
                          key={`sp-${gidx}-${JSON.stringify(gr.spatial_config ?? null)}`}
                          defaultValue={
                            gr.spatial_config
                              ? JSON.stringify(gr.spatial_config, null, 2)
                              : ""
                          }
                          onBlur={(e) => {
                            const t = e.target.value.trim();
                            if (!t) {
                              updateGeometryRule(gidx, { spatial_config: undefined });
                              return;
                            }
                            try {
                              const parsed = JSON.parse(t) as GeometrySpatialRuleConfig;
                              updateGeometryRule(gidx, { spatial_config: parsed });
                            } catch {
                              toast({
                                title: "Неверный JSON",
                                description: "Исправьте spatial_config или очистите поле.",
                                variant: "destructive",
                              });
                            }
                          }}
                        />
                      </div>
                      {isEditing &&
                        isDraftTemplate &&
                        editingId != null &&
                        gr.id != null && (
                          <div className="flex justify-end">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="text-xs"
                              onClick={() =>
                                setGeometrySpatialTarget({
                                  id: gr.id!,
                                  label: `semantic kls ${gr.geometry_semantic_kls_id || "—"}`,
                                })
                              }
                            >
                              Пространственные мета-правила
                            </Button>
                          </div>
                        )}
                    </div>
                  ))}
                </div>
              )}
            </div>

                </TabsContent>

                <TabsContent value="fields" className="mt-0 space-y-6 focus-visible:outline-none">
            {/* Поля */}
            <div>
              <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                <h4 className="text-sm font-semibold">Поля</h4>
                <div className="flex gap-2">
                  {isEditing && isDraftTemplate && (
                    <Button
                      size="sm"
                      variant="secondary"
                      type="button"
                      onClick={() => setProfileWizardOpen(true)}
                      disabled={applyingProfile}
                    >
                      Поле из профиля
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={addField}>
                    <Plus className="h-3.5 w-3.5 mr-1" />
                    Добавить
                  </Button>
                </div>
              </div>
              {fields.length === 0 ? (
                <p className="text-xs text-muted-foreground">Полей нет</p>
              ) : (
                <div className="space-y-2">
                  {fields.map((f, idx) => (
                    <div key={idx} className="space-y-2 border rounded-md p-2">
                      <div className="grid grid-cols-[1fr_1fr_120px_100px_auto] gap-2 items-end">
                        <div>
                          <Label className="text-xs">field_key</Label>
                          <Input
                            value={f.field_key}
                            onChange={(e) =>
                              updateField(idx, { field_key: e.target.value })
                            }
                            className="h-8 text-sm"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Название</Label>
                          <Input
                            value={f.label}
                            onChange={(e) =>
                              updateField(idx, { label: e.target.value })
                            }
                            className="h-8 text-sm"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Тип</Label>
                          <Select
                            value={f.field_type}
                            onValueChange={(v) => {
                              const code = v as CardFieldType;
                              const meta = fieldWidgetTypesQ.data?.find(
                                (w) => w.kls_code === code,
                              );
                              updateField(idx, {
                                field_type: code,
                                field_type_kls_id: meta?.kls_id,
                              });
                            }}
                          >
                            <SelectTrigger className="h-8 text-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {fieldTypeSelectOptions.map(({ value: val, label }) => (
                                <SelectItem key={val} value={val}>
                                  {label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs">qual_id</Label>
                          <Input
                            type="number"
                            value={f.qual_id || ""}
                            onChange={(e) =>
                              updateField(idx, {
                                qual_id: Number(e.target.value) || 0,
                              })
                            }
                            className="h-8 text-sm"
                          />
                        </div>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8 text-destructive"
                          onClick={() => removeField(idx)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                      <div className="grid grid-cols-[minmax(140px,180px)_1fr] gap-2 items-end">
                        <div>
                          <Label className="text-xs">Уровень поля</Label>
                          <Select
                            value={f.applies_to ?? "object"}
                            onValueChange={(v) => {
                              const nextApplies = v as CardAppliesTo;
                              const nextField = { ...f, applies_to: nextApplies };
                              const compatible = sectionsCompatibleWithField(
                                nextField,
                                sections,
                              );
                              const c = (f.section_code ?? "").trim();
                              const sectionStillOk =
                                c.length > 0 &&
                                compatible.some((s) => s.code === c);
                              updateField(idx, {
                                applies_to: nextApplies,
                                section_code: sectionStillOk
                                  ? f.section_code
                                  : firstCompatibleSectionCode(
                                      { applies_to: nextApplies },
                                      sections,
                                    ),
                              });
                            }}
                          >
                            <SelectTrigger className="h-8 text-sm mt-1">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {(Object.keys(APPLIES_TO_LABELS) as CardAppliesTo[]).map(
                                (val) => (
                                  <SelectItem key={val} value={val}>
                                    {APPLIES_TO_LABELS[val]}
                                  </SelectItem>
                                ),
                              )}
                            </SelectContent>
                          </Select>
                        </div>
                        <p className="text-[11px] text-muted-foreground pb-1">
                          «Геометрия» — поля вкладок геометрий; доступны только секции с уровнем
                          «Геометрия».
                        </p>
                      </div>
                      {f.applies_to === "geometry" && (
                        <div className="flex items-center gap-2 pt-1">
                          <Checkbox
                            id={`inherit-${idx}`}
                            checked={f.inherit_from_object ?? false}
                            onCheckedChange={(v) =>
                              updateField(idx, { inherit_from_object: v === true })
                            }
                          />
                          <Label htmlFor={`inherit-${idx}`} className="text-xs font-normal">
                            Наследовать с объекта при пустом значении на геометрии
                          </Label>
                        </div>
                      )}
                      {f.applies_to !== "geometry" && (
                        <div className="flex items-center gap-2 pt-1">
                          <Checkbox
                            id={`inherit-parent-${idx}`}
                            checked={parentObjectInheritanceEnabled(f)}
                            onCheckedChange={(v) =>
                              updateField(idx, {
                                inheritance_config:
                                  v === true
                                    ? {
                                        parent_object: {
                                          enabled: true,
                                          max_depth: 10,
                                          prefer_own: true,
                                        },
                                      }
                                    : undefined,
                              })
                            }
                          />
                          <Label
                            htmlFor={`inherit-parent-${idx}`}
                            className="text-xs font-normal"
                          >
                            Наследовать с родительского объекта (пусто у дочернего;
                            core.objects.parent_object_id)
                          </Label>
                        </div>
                      )}
                      <div>
                        <Label className="text-xs">Секция</Label>
                        <Select
                          value={
                            (() => {
                              const choices = sectionsCompatibleWithField(f, sections);
                              const c = (f.section_code ?? "").trim();
                              if (c && choices.some((s) => s.code === c)) return c;
                              return SECTION_SELECT_AUTO;
                            })()
                          }
                          onValueChange={(v) =>
                            updateField(idx, {
                              section_code:
                                v === SECTION_SELECT_AUTO ? "" : v,
                            })
                          }
                        >
                          <SelectTrigger className="h-8 text-sm mt-1">
                            <SelectValue placeholder="Секция карточки" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={SECTION_SELECT_AUTO}>
                              Авто — первая секция уровня «
                              {APPLIES_TO_LABELS[f.applies_to ?? "object"]}»
                            </SelectItem>
                            {sectionsCompatibleWithField(f, sections)
                              .slice()
                              .sort(
                                (a, b) =>
                                  (a.display_order ?? 0) - (b.display_order ?? 0)
                              )
                              .map((s) => (
                                <SelectItem key={s.code} value={s.code}>
                                  {s.name || s.code}{" "}
                                  <span className="text-muted-foreground font-mono text-[11px]">
                                    ({s.code})
                                  </span>
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                        {sectionsCompatibleWithField(f, sections).length === 0 && (
                          <p className="text-[11px] text-muted-foreground mt-1">
                            Нет секций для этого уровня — добавьте секцию выше.
                          </p>
                        )}
                      </div>
                      <div className="rounded-md border border-dashed p-2 space-y-2">
                        <Label className="text-xs">Зависимость от переключателя</Label>
                        {(() => {
                          const dep = parseFieldDependencyConfig(f.visibility_rules);
                          const toggleCandidates = fields
                            .filter(
                              (it, i2) =>
                                i2 !== idx &&
                                it.field_type === "boolean" &&
                                (it.applies_to ?? "object") ===
                                  (f.applies_to ?? "object")
                            )
                            .map((it) => ({
                              field_key: it.field_key,
                              label: it.label || it.field_key,
                            }));
                          return (
                            <>
                              <div className="flex items-center gap-2">
                                <Checkbox
                                  id={`dep-enabled-${idx}`}
                                  checked={dep.enabled}
                                  onCheckedChange={(v) => {
                                    const next = buildFieldDependencyRules({
                                      ...dep,
                                      enabled: v === true,
                                    });
                                    updateField(idx, { visibility_rules: next });
                                  }}
                                />
                                <Label
                                  htmlFor={`dep-enabled-${idx}`}
                                  className="text-xs font-normal"
                                >
                                  Поле активно только при условии
                                </Label>
                              </div>
                              {dep.enabled && (
                                <div className="grid grid-cols-2 gap-2">
                                  <div>
                                    <Label className="text-[11px]">Поле-переключатель</Label>
                                    <Select
                                      value={dep.depends_on || "__none__"}
                                      onValueChange={(v) => {
                                        const next = buildFieldDependencyRules({
                                          ...dep,
                                          depends_on: v === "__none__" ? "" : v,
                                        });
                                        updateField(idx, { visibility_rules: next });
                                      }}
                                    >
                                      <SelectTrigger className="h-8 text-xs mt-1">
                                        <SelectValue placeholder="Выберите boolean-поле" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="__none__">Не выбрано</SelectItem>
                                        {toggleCandidates.map((it) => (
                                          <SelectItem key={it.field_key} value={it.field_key}>
                                            {it.label}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </div>
                                  <div>
                                    <Label className="text-[11px]">Когда активно</Label>
                                    <Select
                                      value={dep.equals ? "true" : "false"}
                                      onValueChange={(v) => {
                                        const next = buildFieldDependencyRules({
                                          ...dep,
                                          equals: v === "true",
                                        });
                                        updateField(idx, { visibility_rules: next });
                                      }}
                                    >
                                      <SelectTrigger className="h-8 text-xs mt-1">
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="true">Переключатель = Да</SelectItem>
                                        <SelectItem value="false">Переключатель = Нет</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  </div>
                                  <div className="col-span-2">
                                    <Label className="text-[11px]">Поведение поля</Label>
                                    <Select
                                      value={dep.mode}
                                      onValueChange={(v) => {
                                        const next = buildFieldDependencyRules({
                                          ...dep,
                                          mode: v === "hide" ? "hide" : "enable",
                                        });
                                        updateField(idx, { visibility_rules: next });
                                      }}
                                    >
                                      <SelectTrigger className="h-8 text-xs mt-1">
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="enable">
                                          Показывать, но блокировать при невыполненном условии
                                        </SelectItem>
                                        <SelectItem value="hide">
                                          Полностью скрывать при невыполненном условии
                                        </SelectItem>
                                      </SelectContent>
                                    </Select>
                                  </div>
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </div>
                      <Collapsible className="rounded-md border border-dashed p-2">
                        <CollapsibleTrigger className="flex w-full items-center justify-between text-left text-xs font-medium text-muted-foreground hover:text-foreground py-1">
                          Сводка правил (фаза 9)
                          <ChevronsUpDown className="h-4 w-4 shrink-0" />
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <div className="pt-2 space-y-2">
                            <CardTemplateFieldRulesSummary field={f} />
                            {isEditing &&
                              isDraftTemplate &&
                              editingId != null &&
                              f.id != null && (
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  className="text-xs"
                                  onClick={() =>
                                    setRulesTarget({ id: f.id!, label: f.label })
                                  }
                                >
                                  Редактор мета-правил
                                </Button>
                              )}
                          </div>
                        </CollapsibleContent>
                      </Collapsible>
                      {f.field_key.trim() === "obj_status" && (
                        <ObjStatusOptionsEditor
                          options={parseStatusOptions(f.ui_config)}
                          onChange={(next) => {
                            const filtered = next.filter(
                              (r) => r.value.trim() !== ""
                            );
                            if (filtered.length === 0) {
                              const { status_options: _s, ...rest } =
                                f.ui_config ?? {};
                              updateField(idx, {
                                ui_config:
                                  Object.keys(rest).length > 0 ? rest : {},
                              });
                            } else {
                              updateField(idx, {
                                ui_config: {
                                  ...f.ui_config,
                                  status_options: filtered,
                                },
                              });
                            }
                          }}
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
                </TabsContent>

                <TabsContent value="assistant" className="mt-0 space-y-4 focus-visible:outline-none">
                  <TemplateArchitectChatPanel />
                </TabsContent>
              </div>
            </Tabs>
          )}

          <SheetFooter className="shrink-0 gap-2 border-t px-6 py-4 sm:flex-row sm:justify-end">
            <Button type="button" variant="outline" onClick={requestClose}>
              Отмена
            </Button>
            <Button type="button" onClick={handleSubmit} disabled={isSaving}>
              {isSaving ? "Сохранение..." : isEditing ? "Обновить" : "Создать"}
            </Button>
          </SheetFooter>

          {/* Поверх контента (z-index), иначе заголовок/вкладки перехватывают клики по левому краю */}
          <div
            role="separator"
            aria-orientation="vertical"
            aria-label="Изменить ширину панели конструктора"
            className="pointer-events-auto absolute bottom-0 left-0 top-0 z-[100] flex w-3 cursor-col-resize touch-none select-none items-center justify-center border-r border-transparent hover:border-border hover:bg-muted/50"
            onPointerDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleResizePointerDown(e);
            }}
          >
            <GripVertical className="pointer-events-none h-4 w-4 text-muted-foreground" />
          </div>
        </SheetContent>
      </Sheet>

      <AlertDialog open={confirmCloseOpen} onOpenChange={setConfirmCloseOpen}>
        <AlertDialogContent nestedAboveSheet>
          <AlertDialogHeader>
            <AlertDialogTitle>Закрыть без сохранения?</AlertDialogTitle>
            <AlertDialogDescription>
              Изменения в конструкторе шаблона не сохранены. Закрыть панель?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Вернуться</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setConfirmCloseOpen(false);
                onClose();
              }}
            >
              Закрыть без сохранения
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    {isEditing && editingId != null && (
      <FieldFromProfileWizard
        open={profileWizardOpen}
        onOpenChange={setProfileWizardOpen}
        sections={sections}
        fieldWidgetTypes={fieldWidgetTypesQ.data ?? []}
        onApply={applyFieldFromProfile}
        isBusy={applyingProfile}
      />
    )}
    {isEditing && editingId != null && rulesTarget != null && (
      <CardTemplateFieldRulesEditor
        open
        onOpenChange={(o) => {
          if (!o) setRulesTarget(null);
        }}
        templateId={editingId}
        fieldId={rulesTarget.id}
        fieldLabel={rulesTarget.label}
        canEdit={isDraftTemplate}
        onSaved={() => setInitialized(false)}
      />
    )}
    {isEditing && editingId != null && sectionRulesTarget != null && (
      <CardTemplateMetaRulesEditor
        open
        onOpenChange={(o) => {
          if (!o) setSectionRulesTarget(null);
        }}
        templateId={editingId}
        resourceId={sectionRulesTarget.id}
        resourceLabel={sectionRulesTarget.label}
        appliesTo="section"
        queryKeySuffix="section-rules"
        title="Правила секции"
        description={
          "Строки templates.card_section_rules (видимость, роли, сворачивание). " +
          "После сохранения синхронизируются ui_config и колонки секции."
        }
        emptyHint="Нет мета-правил — в API останутся значения из колонок и ui_config до следующей синхронизации."
        fetchRules={() =>
          cardTemplatesApi.getSectionRules(editingId, sectionRulesTarget.id)
        }
        putRules={(body) =>
          cardTemplatesApi.putSectionRules(editingId, sectionRulesTarget.id, body)
        }
        canEdit={isDraftTemplate}
        onSaved={() => setInitialized(false)}
      />
    )}
    {isEditing && editingId != null && geometrySpatialTarget != null && (
      <CardTemplateMetaRulesEditor
        open
        onOpenChange={(o) => {
          if (!o) setGeometrySpatialTarget(null);
        }}
        templateId={editingId}
        resourceId={geometrySpatialTarget.id}
        resourceLabel={geometrySpatialTarget.label}
        appliesTo="geometry_rule"
        queryKeySuffix="geometry-spatial-rules"
        title="Пространственные правила геометрии"
        description={
          "Строки templates.card_geometry_spatial_rules (KLS-ссылки, границы площади/длины и др.). " +
          "После сохранения синхронизируются min/max, is_required и spatial_config строки geometry_rules."
        }
        emptyHint="Нет пространственных правил — сработают только кардинальность и JSON spatial_config из формы выше при сохранении шаблона."
        fetchRules={() =>
          cardTemplatesApi.getGeometrySpatialRules(editingId, geometrySpatialTarget.id)
        }
        putRules={(body) =>
          cardTemplatesApi.putGeometrySpatialRules(
            editingId,
            geometrySpatialTarget.id,
            body
          )
        }
        canEdit={isDraftTemplate}
        onSaved={() => setInitialized(false)}
      />
    )}
    </>
  );
}
